import { Text } from "./Text";
import { Figure, FigureType, ParameteredText } from "../../../../types";
import { mapRelativeToPixels } from "../../../../helpers/itemsHelper";
import { TextEditable } from "./TextEditable";
import { useTemplateEditorStore } from "../../../../store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../../../store/useTemplatesStatusStore";
import { useCallback } from "react";

export const TemplateParameteredTexts = ({
  texts,
  figure,
  previewWidth,
  previewHeight,
  editable = false,
}: {
  texts: ParameteredText[];
  figure?: Figure;
  previewWidth: number;
  previewHeight: number;
  editable?: boolean;
}) => {
  const { updateParameteredText, updateFigure } = useTemplateEditorStore(
    (store) => ({
      updateParameteredText: store.updateParameteredText,
      updateFigure: store.updateFigure,
    })
  );
  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const handleChange = useCallback(
    (params: Partial<ParameteredText>, figureType?: FigureType) => {
      if (figureType) {
        updateFigure(params, figureType);
      } else {
        const { size, ...rest } = params;
        const isContentChanged = !!params.content;

        updateParameteredText(isContentChanged ? rest : params);
      }
      addTemplateUpdate();
    },
    [updateParameteredText, updateFigure, addTemplateUpdate]
  );

  const getTextComponent = (text: ParameteredText, figureType?: FigureType) =>
    editable ? (
      <TextEditable
        key={text.id}
        textItem={text}
        previewWidth={previewWidth}
        previewHeight={previewHeight}
        onChange={(params) => handleChange(params, figureType)}
      />
    ) : (
      <div
        key={text.id}
        style={{
          position: "absolute",
          top: mapRelativeToPixels(text.position.y, previewHeight),
          left: mapRelativeToPixels(text.position.x, previewWidth),
          zIndex: text.layerIndex,
          transformOrigin: "center",
          transform: text.rotation ? `rotate(${text.rotation}deg)` : undefined,
        }}
      >
        <Text
          textItem={text}
          previewWidth={previewWidth}
          previewHeight={previewHeight}
        />
      </div>
    );

  return (
    <>
      {texts
        .filter((text) => text.visible)
        .map((text) => getTextComponent(text))}
      {figure && mapFigure(figure, getTextComponent)}
    </>
  );
};

const mapFigure = (
  figure: Figure,
  componentGetterFn: (
    text: ParameteredText,
    figureType?: FigureType
  ) => React.ReactNode
) => {
  const units = figure.units;

  return (
    <>
      {figure.number.visible && componentGetterFn(figure.number, "number")}
      {figure.number.visible &&
        units?.visible &&
        componentGetterFn(units, "units")}
    </>
  );
};
